import { useEffect, useState } from "react";

/**
 * FOC-203 — the tenant's home page template (server-rendered: welcome + away zones
 * and the background image), or null when the tenant has none (built-in home).
 *
 * Home is the first screen, so avoid flashing the built-in (Barnes) screen before a
 * tenant's own: the last answer per language is cached in localStorage and shown
 * instantly while it revalidates. On a first visit with nothing cached, `ready` stays
 * false for at most WAIT_MS, then falls back to the built-in screen; a late answer
 * still applies (and is cached for next time).
 */

export type HomeTemplate = {
  welcome: string;
  away: string;
  landingImage: string | null;
};

const CACHE_PREFIX = "focus.homeTemplate.";
const WAIT_MS = 400;

function readCache(lang: string): HomeTemplate | null | undefined {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + lang);
    if (raw == null) return undefined; // nothing cached
    return JSON.parse(raw) as HomeTemplate | null;
  } catch {
    return undefined;
  }
}

function writeCache(lang: string, value: HomeTemplate | null) {
  try {
    localStorage.setItem(CACHE_PREFIX + lang, JSON.stringify(value));
  } catch {
    /* storage full/blocked — the live answer still renders */
  }
}

export function useHomeTemplate(languageCode: string): {
  ready: boolean;
  home: HomeTemplate | null;
} {
  const lang = (languageCode || "en").toLowerCase();
  const cached = readCache(lang);
  const [home, setHome] = useState<HomeTemplate | null>(cached ?? null);
  const [ready, setReady] = useState<boolean>(cached !== undefined);

  useEffect(() => {
    let cancelled = false;
    const fallback = window.setTimeout(
      () => !cancelled && setReady(true),
      WAIT_MS
    );

    fetch(`/api/page/home?lang=${encodeURIComponent(lang)}`)
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((body) => {
        if (cancelled) return;
        const value: HomeTemplate | null = body && body.data ? body.data : null;
        writeCache(lang, value);
        setHome(value);
        setReady(true);
      })
      .catch(() => {
        if (!cancelled) setReady(true); // keep whatever we have (cache or built-in)
      });

    return () => {
      cancelled = true;
      window.clearTimeout(fallback);
    };
  }, [lang]);

  return { ready, home };
}
